import * as THREE from "three";
import { COMPASS_RADIUS, COMPASS_X, COMPASS_Z, NORTH_YAW } from "./config";
import { makeLabel } from "./labels";

const RING = 0xf2efe6;
const NORTH = 0xe24b4b;
const PAD = 0x1c242c;

export function buildCompass(): THREE.Group {
  const g = new THREE.Group();
  g.name = "compass";
  g.position.set(COMPASS_X, 0, COMPASS_Z);
  g.rotation.y = NORTH_YAW;

  const pad = new THREE.Mesh(
    new THREE.CylinderGeometry(COMPASS_RADIUS + 1.2, COMPASS_RADIUS + 1.4, 0.18, 48),
    new THREE.MeshStandardMaterial({ color: PAD, roughness: 0.86, metalness: 0.06 }),
  );
  pad.position.y = 0.1;
  pad.receiveShadow = true;
  g.add(pad);

  const disc = new THREE.Mesh(
    new THREE.CircleGeometry(COMPASS_RADIUS, 64),
    new THREE.MeshStandardMaterial({
      color: 0x2a333c,
      roughness: 0.7,
      metalness: 0.08,
    }),
  );
  disc.rotation.x = -Math.PI / 2;
  disc.position.y = 0.2;
  g.add(disc);

  const ring = new THREE.Mesh(
    new THREE.TorusGeometry(COMPASS_RADIUS, 0.12, 8, 64),
    new THREE.MeshStandardMaterial({ color: RING, roughness: 0.35, metalness: 0.2 }),
  );
  ring.rotation.x = Math.PI / 2;
  ring.position.y = 0.22;
  g.add(ring);

  g.add(axisBar("ns", COMPASS_RADIUS - 0.4, 0.28, 0.08, NORTH));
  g.add(axisBar("ew", COMPASS_RADIUS - 0.6, 0.12, 0.06, RING));

  for (let i = 0; i < 16; i++) {
    if (i % 4 === 0) continue;
    const tick = new THREE.Mesh(
      new THREE.BoxGeometry(0.08, 0.08, i % 2 === 0 ? 1.4 : 0.8),
      new THREE.MeshBasicMaterial({ color: RING }),
    );
    const a = (i / 16) * Math.PI * 2;
    const r = COMPASS_RADIUS - 0.9;
    tick.position.set(Math.sin(a) * r, 0.26, Math.cos(a) * r);
    tick.rotation.y = a;
    g.add(tick);
  }

  g.add(northNeedle());

  const n = makeLabel("N", "cardinal-label cardinal-n");
  n.position.set(0, 2.6, COMPASS_RADIUS + 1.4);
  g.add(n);

  const s = makeLabel("S", "cardinal-label");
  s.position.set(0, 2.6, -COMPASS_RADIUS - 1.4);
  g.add(s);

  const e = makeLabel("E", "cardinal-label");
  e.position.set(COMPASS_RADIUS + 1.4, 2.6, 0);
  g.add(e);

  const w = makeLabel("W", "cardinal-label");
  w.position.set(-COMPASS_RADIUS - 1.4, 2.6, 0);
  g.add(w);

  const northWord = makeLabel("NORTH", "cardinal-word");
  northWord.position.set(0, 3.4, COMPASS_RADIUS + 6);
  g.add(northWord);

  return g;
}

function axisBar(
  dir: "ns" | "ew",
  length: number,
  thick: number,
  height: number,
  color: number,
): THREE.Mesh {
  const mesh = new THREE.Mesh(
    new THREE.BoxGeometry(
      dir === "ew" ? length * 2 : thick,
      height,
      dir === "ns" ? length * 2 : thick,
    ),
    new THREE.MeshStandardMaterial({ color, roughness: 0.4, metalness: 0.15 }),
  );
  mesh.position.y = 0.26;
  return mesh;
}

function northNeedle(): THREE.Mesh {
  const r = COMPASS_RADIUS - 0.5;
  const geo = new THREE.BufferGeometry();
  geo.setAttribute(
    "position",
    new THREE.Float32BufferAttribute(
      [0, 0.34, r, 1.15, 0.34, 1.4, -1.15, 0.34, 1.4],
      3,
    ),
  );
  geo.computeVertexNormals();
  return new THREE.Mesh(
    geo,
    new THREE.MeshStandardMaterial({
      color: NORTH,
      roughness: 0.35,
      metalness: 0.1,
      side: THREE.DoubleSide,
    }),
  );
}
